/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { FC, ReactNode } from 'react';
import { map } from 'lodash';
import cx from 'classnames';

import messages from './FulfillmentCardHeader.messages';
import {
  IFulfillmentAddress,
  IOrder,
  IOrderFulfillment,
  IShipment
} from '@broadleaf/admin-components/dist/types/oms';
import {
  Currency,
  useFormatDate,
  useFormatMessage,
  useFormatTime
} from '@broadleaf/admin-components/dist/common';
import { PopoverLabel } from '@broadleaf/admin-components/dist/oms/components';
import { ICommonMonetaryAmount } from '@broadleaf/admin-components/dist/types/common';
import { HelpTextProps } from '@broadleaf/admin-components/dist/common/components/HelpText';
import { buildHelpText } from '@src/components/form/lookup/LookupField';

export interface FulfillmentCardHeaderProps {
  fulfillment: IOrderFulfillment;
  order: IOrder;
  [key: string]: any;
}

export const FulfillmentCardHeader: FC<FulfillmentCardHeaderProps> = ({
  children,
  fulfillment,
  order
}) => {
  const formatMessage = useFormatMessage();

  return (
    // using negative margin on medium to offset the margin on headerDetails
    // which keep the bottom margin since they can wrap
    // the right padding is the offset for the show/hide button
    <div className="FulfillmentCardHeader tw-flex tw-w-full tw-flex-1 tw-flex-col md:tw--mb-3 md:tw-flex-row md:tw-pr-12 xl:tw-mb-0">
      <div className="tw-flex tw-w-full tw-flex-row tw-flex-wrap md:tw-w-4/5">
        <FulfillmentCardHeaderDetail
          label={formatMessage(messages.referenceNumber)}
        >
          <span className="tw-text-sm tw-font-medium">
            <a
              className="tw-text-link-600 hover:tw-text-link-700"
              href={`/fulfillments/${fulfillment.id}`}
              target="_blank"
              rel="noreferrer"
            >
              <span title={fulfillment.orderFulfillmentNumber}>
                {fulfillment.orderFulfillmentNumber}
              </span>
            </a>
          </span>
        </FulfillmentCardHeaderDetail>
        {fulfillment.address && (
          <FulfillmentCardHeaderDetail
            label={formatMessage(messages.fulfillmentAddressDetail)}
          >
            <FulfillmentCardDeliverTo address={fulfillment.address} />
          </FulfillmentCardHeaderDetail>
        )}
        <FulfillmentCardHeaderDetail
          label={formatMessage(messages.sellerFacilityName)}
        >
          <span className="tw-truncate tw-text-sm tw-font-medium">
            {fulfillment.attributes.vendor}
          </span>
        </FulfillmentCardHeaderDetail>
        {fulfillment.fulfillmentOption &&
          (fulfillment.fulfillmentOption.description ||
            fulfillment.fulfillmentOption.name) && (
            <FulfillmentCardHeaderDetail
              label={formatMessage(messages.fulfillmentMethodDetail)}
            >
              <span
                className="tw-truncate tw-text-sm tw-font-medium tw-text-gray-700"
                title={
                  fulfillment.fulfillmentOption.description ||
                  fulfillment.fulfillmentOption.name
                }
              >
                {fulfillment.fulfillmentOption.description ||
                  fulfillment.fulfillmentOption.name}
              </span>
            </FulfillmentCardHeaderDetail>
          )}
        {fulfillment.shipment?.trackingNumber && (
          <FulfillmentCardHeaderDetail
            label={formatMessage(messages.shippingTrackingNumber)}
          >
            <FulfillmentCardShipment shipment={fulfillment.shipment} />
          </FulfillmentCardHeaderDetail>
        )}
        {/*{order.paymentDistributionDetails && (*/}
        {/*  <FulfillmentCardHeaderDetail*/}
        {/*    label={formatMessage(messages.fulfillmentPaymentDistributionDetail)}*/}
        {/*    helpText={formatMessage(*/}
        {/*      messages.fulfillmentPaymentDistributionDetailHelpText*/}
        {/*    )}*/}
        {/*  >*/}
        {/*    <FulfillmentCardPaymentDistribution*/}
        {/*      order={order}*/}
        {/*      fulfillment={fulfillment}*/}
        {/*    />*/}
        {/*  </FulfillmentCardHeaderDetail>*/}
        {/*)}*/}
        {children}
        <FulfillmentCardHeaderDetail
          label={formatMessage(messages.fulfillmentTotalDetail)}
        >
          <FulfillmentCardTotal fulfillment={fulfillment} />
        </FulfillmentCardHeaderDetail>
      </div>
    </div>
  );
};

export interface FulfillmentCardDeliverToProps {
  address: IFulfillmentAddress;
}

export const FulfillmentCardDeliverTo: FC<FulfillmentCardDeliverToProps> = ({
  address
}) => {
  return (
    <PopoverLabel label={address.fullName} position="bottom-left">
      <div className="tw-p-2 tw-font-semibold">
        {address.fullName || `${address.firstName} ${address.lastName}`}
      </div>
      <hr className="tw-border-gray-300" />
      <FulfillmentCardAddress address={address} />
    </PopoverLabel>
  );
};

export interface FulfillmentCardShipmentProps {
  shipment: IShipment;
}

export const FulfillmentCardShipment: FC<FulfillmentCardShipmentProps> = ({
  shipment
}) => {
  const formatTime = useFormatTime();
  const formatDate = useFormatDate();
  const formatMessage = useFormatMessage();
  return (
    <PopoverLabel
      label={shipment.trackingNumber}
      position="bottom-left"
      width="tw-w-96"
    >
      <div className="tw-space-y-2 tw-p-2 tw-text-sm tw-font-semibold">
        {shipment.shipperType && (
          <div>
            {formatMessage(messages.shippingShipperTypeLabel, {
              type: (
                <span className="tw-font-normal">
                  {formatMessage(messages.shippingShipperTypeValue, {
                    type: shipment.shipperType
                  })}
                </span>
              )
            })}
          </div>
        )}
        {shipment.shipDate && (
          <div>
            {formatMessage(messages.shippingShipDate, {
              date: (
                <span className="tw-font-normal">
                  {formatDate(shipment.shipDate, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              ),
              time: (
                <span className="tw-font-normal">
                  {formatTime(shipment.shipDate, {
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZoneName: 'short'
                  })}
                </span>
              )
            })}
          </div>
        )}
        {shipment.trackingUrl && (
          <div>
            {formatMessage(messages.shippingTrackingUrl, {
              url: (
                <a
                  className="tw-font-normal tw-text-link-600 hover:tw-text-link-700 focus:tw-text-link-700 focus:tw-outline-none"
                  href={shipment.trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {shipment.trackingUrl}
                </a>
              )
            })}
          </div>
        )}
      </div>
    </PopoverLabel>
  );
};

export interface FulfillmentCardTotalProps {
  fulfillment: IOrderFulfillment;
}

export const FulfillmentCardTotal: FC<FulfillmentCardTotalProps> = ({
  fulfillment
}) => {
  const formatMessage = useFormatMessage();
  const {
    fulfillmentTotal,
    merchandiseTotal,
    grandTotal,
    taxTotal
  } = fulfillment;
  return (
    <PopoverLabel
      label={
        <Currency currency={grandTotal.currency} value={grandTotal.amount} />
      }
      position="bottom-left"
    >
      <div className="tw-p-2">
        {merchandiseTotal && (
          <FulfillmentCardTotalDetail
            className="tw-mb-1 tw-text-sm"
            label={formatMessage(messages.merchandiseTotal)}
            total={merchandiseTotal}
          />
        )}
        {fulfillmentTotal && (
          <FulfillmentCardTotalDetail
            className="tw-mb-1 tw-text-sm"
            label={formatMessage(messages.fulfillmentTotal)}
            total={fulfillmentTotal}
          />
        )}
        {taxTotal && (
          <FulfillmentCardTotalDetail
            className="tw-mb-1 tw-text-sm"
            label={formatMessage(messages.taxTotal)}
            total={taxTotal}
          />
        )}
      </div>
      <hr className="tw-border-gray-300" />
      <div className="tw-p-2">
        <FulfillmentCardTotalDetail
          className="tw-text-base"
          label={formatMessage(messages.grandTotal)}
          total={grandTotal}
        />
      </div>
    </PopoverLabel>
  );
};

export interface FulfillmentCardTotalDetailProps {
  className?: string;
  label: ReactNode;
  total: ICommonMonetaryAmount;
}

export const FulfillmentCardTotalDetail: FC<FulfillmentCardTotalDetailProps> = ({
  className,
  label,
  total
}) => {
  return (
    <div className={cx(className, 'tw-flex tw-justify-between')}>
      <div className="tw-text-gray-700">{label}</div>
      <div className="tw-font-medium tw-text-gray-900">
        <Currency currency={total.currency} value={total.amount} />
      </div>
    </div>
  );
};

export interface FulfillmentCardAddressProps {
  address: IFulfillmentAddress;
}

export const FulfillmentCardAddress: FC<FulfillmentCardAddressProps> = ({
  address
}) => {
  const formatMessage = useFormatMessage();
  return (
    <ul className="tw-list-none tw-p-2 tw-text-sm">
      <li>
        {`${address.addressLine1} ${address.addressLine2 || ''} ${
          address.addressLine3 || ''
        }`.trim()}
      </li>
      <li>
        {address.city}, {address.stateProvinceRegion} {address.postalCode}
      </li>
      <li>{address.country}</li>
      {address.phonePrimary && (
        <li>
          {formatMessage(messages.fulfillmentAddressDetailPhone, {
            phoneNumber: address.phonePrimary.phoneNumber
          })}
        </li>
      )}
    </ul>
  );
};

interface FulfillmentCardHeaderDetailProps {
  className?: string;
  label: string;
  helpText?: string | HelpTextProps;
}

export const FulfillmentCardHeaderDetail: React.FC<FulfillmentCardHeaderDetailProps> = ({
  children,
  className,
  label,
  helpText
}) => {
  return (
    <div
      className={cx(
        className,
        'tw-mb-3 tw-mr-5 tw-flex tw-flex-col tw-items-start tw-text-left xl:tw-mb-0'
      )}
    >
      <span className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-tight tw-text-gray-600">
        {label} {!!helpText && buildHelpText(helpText)}
      </span>
      {children}
    </div>
  );
};

interface FulfillmentCardPaymentDistributionProps {
  order: IOrder;
  fulfillment: IOrderFulfillment;
  helpText?: string | HelpTextProps;
}

export const FulfillmentCardPaymentDistribution: React.FC<FulfillmentCardPaymentDistributionProps> = ({
  order,
  fulfillment
}) => {
  return (
    <PopoverLabel
      label={
        <Currency
          currency={fulfillment.currency}
          value={fulfillment.grandTotal.amount}
        />
      }
      position="bottom-left"
    >
      <div className="tw-list-none tw-p-2 tw-text-sm">
        {map(order.paymentDistributionDetails, detail => (
          <div className="tw-flex tw-justify-between">
            <div className="tw-text-gray-700">{detail.paymentDisplayType}</div>
            <div className="tw-font-medium tw-text-gray-900">
              <Currency
                currency={fulfillment.currency}
                value={detail.ratio * fulfillment.grandTotal.amount}
              />
            </div>
          </div>
        ))}
      </div>
    </PopoverLabel>
  );
};

export default FulfillmentCardHeader;
